import { Resources } from 'constants/resources';

import { useMemo } from 'react';

import { hydraSchemaAnalyzer } from '@api-platform/admin';
import { useNavigateToRoot } from 'hooks/useNavigateToRoot';
import { useRecordModal } from 'hooks/useRecordModal';
import { GatewayRulesForm } from 'pages/GatewayRules/components/GatewayRulesForm';
import { GatewayRulesFormValues } from 'pages/GatewayRules/components/GatewayRulesForm/hooks/useGatewayRulesForm';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { HttpError, useDataProvider, useGetOne, useNotify } from 'react-admin';
import { DeepPartial } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { CancelButton, SaveButtonValidated, DeleteButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card/CardActionsButtons/CardActionsButtons';
import { PageLoading } from 'shared/react-admin/Loading';
import { EditPage } from 'shared/react-admin/Pages/EditPage/EditPage';
import { GatewayRule, MappingItem } from 'types/embedded/gatewayRule';
import { getId } from 'utils';

import { GatewayRuleModalDelete } from './components/GatewayRuleModalDelete/GatewayRuleModalDelete';
import { GatewayRulesFormProvider } from './components/GatewayRulesForm/context/GatewayRulesFormContext';
import { useConditionGroups, useExpressionParams } from './hooks';
import { getInitialEditFormValues } from './utils/getInitialEditFormValues';
import { prepareGatewayRuleFormData } from './utils/prepareGatewayRuleFormData';

export const GatewayRulesEdit = (): JSX.Element => {
  const translate = useTypedTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { toRoot } = useNavigateToRoot();

  const deleteModal = useRecordModal();

  const { id } = useParams();
  const { data, isLoading } = useGetOne<GatewayRule>(Resources.GatewayRules, { id });

  const mappings = data?.mappings ?? [];
  const mappingItems = mappings.flatMap((mapping) => mapping.mappingItems ?? []) as MappingItem[];

  const { expressionParams, loadingExpressionParams } = useExpressionParams();
  const { conditionGroup, isLoadingConditionGroup } = useConditionGroups(
    data?.conditionGroup?.['@id'],
  );
  const isReady = !isLoading && !isLoadingConditionGroup && !loadingExpressionParams;

  const defaultValues = useMemo<DeepPartial<GatewayRulesFormValues> | undefined>(() => {
    if (!isReady || !data) {
      return {};
    }

    return getInitialEditFormValues({
      gatewayRule: data,
      mappingItems,
      mappings,
      conditionGroup,
    });
  }, [isReady, data, conditionGroup]);

  async function onSubmit(appGatewayRulesFormValues: GatewayRulesFormValues) {
    try {
      if (!id) {
        return;
      }
      const modifiedData = prepareGatewayRuleFormData(appGatewayRulesFormValues);

      await dataProvider.update(Resources.GatewayRules, {
        id,
        data: {
          id: parseInt(getId(id), 10),
          conditionGroup: null,
          ...modifiedData,
        },
        previousData: data,
      });

      notify(translate('app.gatewayRules.ruleEdited'), { type: 'info' });
      toRoot();
    } catch (error) {
      const violations = hydraSchemaAnalyzer().getSubmissionErrors(error as HttpError);

      notify(
        violations
          ? `${translate('app.common.error')}: ${Object.entries(violations)
              .map((violation) => {
                if (violation[0] === 'name') {
                  return `${violation[0]}: ${violation[1]}`;
                }
                return violation[1];
              })
              .join('\n')}`
          : `${translate('app.common.error')}:  ${(error as Error).message}`,
        {
          type: 'error',
          multiLine: true,
        },
      );
    }
  }

  if (!isReady) {
    return <PageLoading />;
  }

  return (
    <EditPage
      listName={translate('app.gatewayRules.titleText')}
      title={translate('app.gatewayRules.editRule')}
      width="auto"
    >
      <GatewayRulesFormProvider
        value={{
          loadingExpressionParams,
          expressionParams,
        }}
      >
        <GatewayRulesForm defaultValues={defaultValues} onSubmit={onSubmit}>
          <CardActionsButtons
            leftActionsSlot={
              <DeleteButton
                label={translate('app.gatewayRules.deleteRuleBtn')}
                onClick={deleteModal.handleOpenRecordModal}
                variant="outlined"
              />
            }
            rightActionsSlot={[
              <CancelButton key="cancel-button" linkType="back" variant="outlined" />,
              <SaveButtonValidated
                key="save-button"
                label={translate('app.gatewayRules.saveRule')}
              />,
            ]}
            sx={{ padding: '24px 0' }}
          />
        </GatewayRulesForm>
      </GatewayRulesFormProvider>
      {deleteModal.openRecordModal && data?.id && data?.merchant && (
        <GatewayRuleModalDelete
          id={data?.id}
          merchantName={data?.merchant?.name}
          onClose={deleteModal.handleCloseRecordModal}
          open={deleteModal.openRecordModal}
        />
      )}
    </EditPage>
  );
};
